import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import Swal from 'sweetalert2';
import api3004 from '../../services/api3004';

/* ===== Animação ===== */

const fadeScaleIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.7);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

/* ===== Estilos ===== */

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 20px 30px;
  width: 500px;
  max-width: 90%;
  animation: ${fadeScaleIn} 0.3s ease-out;
  box-shadow: 0 8px 20px rgba(0,0,0,0.25);
`;

const Title = styled.h2`
  margin-bottom: 20px;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Input = styled.input`
  padding: 10px;
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    border-color: #3085d6;
    outline: none;
    box-shadow: 0 0 0 2px rgba(51, 153, 255, 0.2);
  }
`;

const Select = styled.select`
  padding: 10px;
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    border-color: #3085d6;
    outline: none;
    box-shadow: 0 0 0 2px rgba(51, 153, 255, 0.2);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s ease;
  background: ${(props) => (props.primary ? '#3085d6' : '#dcdcdc')};
  color: ${(props) => (props.primary ? '#fff' : '#000')};

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    opacity: 0.8;
  }
`;

/* ===== Validação Placa ===== */

const validatePlaca = (placa) => {
  const regex = /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$|^[A-Z]{3}-?[0-9]{4}$/;
  return regex.test(placa.toUpperCase());
};

const formatPlaca = (value) => {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7);
};

/* ===== Componente ===== */

const BusFormCreate = ({ onClose, onCreated, onibus = {} }) => {

  const [form, setForm] = useState({
    nome: onibus.nome || "",
    prefixo: onibus.prefixo || "",
    placa: onibus.placa || "",
    faturamento_dividido: onibus.faturamento_dividido ?? 0
  });

  useEffect(() => {
    if (onibus.placa) {
      setForm(prev => ({
        ...prev,
        placa: formatPlaca(onibus.placa)
      }));
    }
  }, [onibus.placa]);

  const handleChange = (e) => {
    let { name, value } = e.target;

    const limits = {
      nome: 100,
      prefixo: 20,
      placa: 10
    };

    if (name === "placa") value = formatPlaca(value);

    if (limits[name] && value.length > limits[name]) return;

    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.nome) {
      Swal.fire({
        icon: 'error',
        title: 'Campo obrigatório',
        text: 'O campo Nome é obrigatório!'
      });
      return;
    }

    if (!form.prefixo) {
      Swal.fire({
        icon: 'error',
        title: 'Campo obrigatório',
        text: 'O campo Prefixo é obrigatório!'
      });
      return;
    }

    if (!form.placa) {
      Swal.fire({
        icon: 'error',
        title: 'Campo obrigatório',
        text: 'O campo Placa é obrigatório!'
      });
      return;
    }

    if (!validatePlaca(form.placa)) {
      Swal.fire({
        icon: 'error',
        title: 'Placa inválida',
        text: 'Digite uma placa válida!'
      });
      return;
    }

    try {

      const token = localStorage.getItem("token");

      const payload = {
        nome: form.nome,
        prefixo: Number(form.prefixo),
        placa: form.placa.toUpperCase(),
        faturamento_dividido: Number(form.faturamento_dividido)
      };

      if (onibus?.id) {

        await api3004.put(`/api/onibus/${onibus.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        Swal.fire({
          icon: "success",
          title: "Sucesso!",
          text: "Ônibus atualizado com sucesso!",
          timer: 1800,
          showConfirmButton: false
        });

      } else {

        await api3004.post("/api/onibus", payload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        Swal.fire({
          icon: "success",
          title: "Sucesso!",
          text: "Ônibus criado com sucesso!",
          timer: 1800,
          showConfirmButton: false
        });

      }

      onCreated();
      onClose();

    } catch (err) {

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: err.response?.data?.message || err.message || "Erro ao salvar ônibus"
      });

    }
  };

  return (
    <Overlay>
      <Modal>

        <Title>{onibus.nome ? "Edição" : "Cadastro"} de Ônibus</Title>

        <Form onSubmit={handleSubmit}>

          <Input
            name="nome"
            value={form.nome}
            onChange={handleChange}
            placeholder="Nome do Ônibus"
            maxLength={100}
          />

          <Input
            type="text"
            name="prefixo"
            value={form.prefixo}
            onChange={handleChange}
            placeholder="Prefixo do ônibus"
            maxLength={20}
          />

          <Input
            name="placa"
            value={form.placa}
            onChange={handleChange}
            placeholder="Placa"
            autoComplete="off"
            maxLength={10}
          />

          <Select
            name="faturamento_dividido"
            value={form.faturamento_dividido}
            onChange={handleChange}
          >
            <option value={0}>Faturamento Normal</option>
            <option value={1}>Faturamento Dividido</option>
          </Select>

          <ButtonRow>

            <Button type="button" onClick={onClose}>
              Cancelar
            </Button>

            <Button type="submit" primary>
              {onibus.nome ? "Editar" : "Criar"}
            </Button>

          </ButtonRow>

        </Form>

      </Modal>
    </Overlay>
  );

};

export default BusFormCreate;